import { useState, useEffect, useMemo } from 'react';
import { Keyboard, RotateCcw, AlertTriangle, CheckCircle2, Save } from 'lucide-react';
import {
  CustomShortcut,
  SHORTCUT_ACTIONS,
  SHORTCUT_PRESETS,
  ShortcutAction,
  loadCustomShortcuts,
  saveCustomShortcuts,
  loadCurrentPreset,
  applyPreset,
  resetToDefaults,
  detectConflicts,
  validateShortcut,
  formatShortcutDisplay,
} from '../lib/shortcutCustomization';
import { isMac } from '../hooks/useKeyboardShortcuts';

interface ShortcutEditorProps {
  action: ShortcutAction;
  shortcut: CustomShortcut;
  onUpdate: (actionId: string, shortcut: CustomShortcut) => void;
  conflicts: string[];
  isMacPlatform: boolean;
}

function ShortcutEditor({ action, shortcut, onUpdate, conflicts, isMacPlatform }: ShortcutEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempShortcut, setTempShortcut] = useState<CustomShortcut>(shortcut);
  const [error, setError] = useState<string | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isEditing) return;

    e.preventDefault();
    e.stopPropagation();

    // Ignore modifier-only presses
    if (['Control', 'Meta', 'Shift', 'Alt'].includes(e.key)) {
      return;
    }

    const newShortcut: CustomShortcut = {
      id: action.id,
      key: e.key,
      ctrlOrCmd: isMacPlatform ? e.metaKey : e.ctrlKey,
      shift: e.shiftKey,
      alt: e.altKey,
    };

    setTempShortcut(newShortcut);
    setError(null);
  };

  const handleSave = () => {
    onUpdate(action.id, tempShortcut);
    setIsEditing(false);
    setError(null);
  };

  const handleCancel = () => {
    setTempShortcut(shortcut);
    setIsEditing(false);
    setError(null);
  };

  const displayShortcut = isEditing ? tempShortcut : shortcut;
  const hasConflicts = conflicts.length > 0;

  return (
    <div className="flex items-center justify-between gap-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{action.label}</h4>
          {hasConflicts && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
              <AlertTriangle className="w-3 h-3" /> Conflict
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{action.description}</p>
        {hasConflicts && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            Conflicts with: {conflicts.join(', ')}
          </p>
        )}
        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <input
              type="text"
              value={formatShortcutDisplay(displayShortcut, isMacPlatform)}
              onKeyDown={handleKeyDown}
              placeholder="Press keys..."
              autoFocus
              readOnly
              className="w-32 px-3 py-2 text-sm font-mono text-center border border-blue-500 dark:border-blue-400 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            <button
              onClick={handleSave}
              className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
              title="Save"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancel}
              className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Cancel"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <kbd className="px-3 py-2 text-sm font-mono font-semibold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100">
              {formatShortcutDisplay(displayShortcut, isMacPlatform)}
            </kbd>
            {action.allowCustomization ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              >
                Edit
              </button>
            ) : (
              <span className="px-2 py-1 text-xs text-gray-400 dark:text-gray-500" title="This shortcut cannot be customized">System</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function ShortcutCustomizationPanel() {
  const [shortcuts, setShortcuts] = useState<Record<string, CustomShortcut>>(() => loadCustomShortcuts());
  const [currentPreset, setCurrentPreset] = useState<string>(() => loadCurrentPreset());
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const isMacPlatform = isMac();

  useEffect(() => {
    // Load shortcuts on mount
    const loaded = loadCustomShortcuts();
    // Defer state update to avoid synchronous setState in effect
    setTimeout(() => {
      setShortcuts(loaded);
    }, 0);
  }, []);

  const conflicts = useMemo(() => {
    const detected = detectConflicts(shortcuts);
    const conflictMap = new Map<string, string[]>();

    detected.forEach(({ action1, action2 }) => {
      const action1Label = SHORTCUT_ACTIONS.find((a) => a.id === action1)?.label || action1;
      const action2Label = SHORTCUT_ACTIONS.find((a) => a.id === action2)?.label || action2;

      if (!conflictMap.has(action1)) {
        conflictMap.set(action1, []);
      }
      if (!conflictMap.has(action2)) {
        conflictMap.set(action2, []);
      }

      conflictMap.get(action1)!.push(action2Label);
      conflictMap.get(action2)!.push(action1Label);
    });

    return conflictMap;
  }, [shortcuts]);

  const handleUpdateShortcut = (actionId: string, newShortcut: CustomShortcut) => {
    const validation = validateShortcut(actionId, newShortcut, shortcuts);
    
    if (!validation.valid) {
      // Show error - handled in ShortcutEditor
      return;
    }

    const updated = { ...shortcuts, [actionId]: newShortcut };
    setShortcuts(updated);
    setHasChanges(true);
    setCurrentPreset('custom');
  };

  const handleApplyPreset = (presetId: string) => {
    const presetShortcuts = applyPreset(presetId);
    setShortcuts(presetShortcuts);
    setCurrentPreset(presetId);
    setHasChanges(false);
    setSaveMessage(`Applied "${SHORTCUT_PRESETS.find((p) => p.id === presetId)?.name}" preset`);
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const handleReset = () => {
    if (!confirm('Reset all keyboard shortcuts to defaults? Any custom bindings will be lost.')) return;
    const defaults = resetToDefaults();
    setShortcuts(defaults);
    setCurrentPreset('default');
    setHasChanges(false);
    setSaveMessage('Reset to default shortcuts');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const handleSave = () => {
    saveCustomShortcuts(shortcuts);
    setHasChanges(false);
    setSaveMessage('Shortcuts saved successfully');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  // Group actions by category
  const groupedActions = useMemo(() => {
    const groups = new Map<string, ShortcutAction[]>();
    
    SHORTCUT_ACTIONS.forEach((action) => {
      if (!groups.has(action.category)) {
        groups.set(action.category, []);
      }
      groups.get(action.category)!.push(action);
    });

    return Array.from(groups.entries());
  }, []);

  const hasConflicts = conflicts.size > 0;

  return (
    <div className="w-full max-w-6xl mx-auto p-6 space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 inline-flex items-center gap-2">
              <Keyboard className="w-7 h-7" />
              Keyboard Shortcut Customization
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Customize keyboard shortcuts to match your workflow
            </p>
          </div>
        </div>

        {/* Preset Selection */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Quick Presets</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {SHORTCUT_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handleApplyPreset(preset.id)}
                className={`p-3 text-left border rounded-lg transition-colors ${
                  currentPreset === preset.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{preset.name}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{preset.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Save/Reset Actions */}
        <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 dark:text-amber-400">
                You have unsaved changes
              </span>
            )}
            {hasConflicts && (
              <span className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                <AlertTriangle className="w-4 h-4" />
                {conflicts.size} conflict{conflicts.size !== 1 ? 's' : ''} detected
              </span>
            )}
            {saveMessage && (
              <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="w-4 h-4" />
                {saveMessage}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || hasConflicts}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </div>

        {/* Shortcut List */}
        <div className="space-y-6">
          {groupedActions.map(([category, actions]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                {category}
              </h3>
              <div className="space-y-2">
                {actions.map((action) => (
                  <ShortcutEditor
                    key={action.id}
                    action={action}
                    shortcut={shortcuts[action.id]}
                    onUpdate={handleUpdateShortcut}
                    conflicts={conflicts.get(action.id) || []}
                    isMacPlatform={isMacPlatform}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-900 dark:text-blue-100">
            <strong>Tip:</strong> Click "Edit" next to any shortcut, then press the key combination you want to use.
            The system will automatically detect conflicts and prevent you from saving invalid combinations.
          </p>
        </div>
      </div>
    </div>
  );
}
